<template>
  <!-- Skeleton loader -->
  <div v-if="loader" class="imovel-detalhes">
    <!-- Gallery skeleton -->
    <div class="galeria-section">
      <div class="container">
        <q-skeleton type="QBtn" class="q-mb-md" width="40px" />
        <div class="row q-col-gutter-md">
          <!-- Main image -->
          <div class="col-12 col-md-8">
            <q-skeleton height="400px" class="rounded-borders" />
          </div>
          <!-- Thumbnails -->
          <div class="col-12 col-md-4">
            <div class="thumbnails-grid">
              <q-skeleton
                v-for="n in 4"
                :key="n"
                height="180px"
                class="rounded-borders"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info skeleton -->
    <div class="info-section q-py-xl">
      <div class="container">
        <div class="row q-col-gutter-xl">
          <!-- Left column -->
          <div class="col-12 col-md-8">
            <q-skeleton type="text" width="70%" height="36px" class="q-mb-sm" />
            <q-skeleton type="text" width="40%" class="q-mb-md" />
            <q-skeleton type="text" width="30%" height="48px" class="q-mb-lg" />
            <q-separator class="q-mb-xl" />

            <q-skeleton type="text" width="25%" height="24px" class="q-mb-md" />
            <div class="row q-col-gutter-md q-mb-xl">
              <div class="col-6 col-sm-4 col-md-3" v-for="n in 4" :key="n">
                <q-skeleton height="100px" class="rounded-borders" />
              </div>
            </div>

            <q-skeleton type="text" width="20%" height="24px" class="q-mb-md" />
            <q-skeleton type="text" width="100%" class="q-mb-xs" />
            <q-skeleton type="text" width="100%" class="q-mb-xs" />
            <q-skeleton type="text" width="90%" class="q-mb-xs" />
            <q-skeleton type="text" width="80%" />
          </div>

          <!-- Right column — contact card -->
          <div class="col-12 col-md-4">
            <q-card flat bordered class="sticky-contact-card">
              <q-card-section class="text-center">
                <q-skeleton
                  type="QAvatar"
                  size="80px"
                  class="q-mx-auto q-mb-md"
                />
                <q-skeleton type="text" width="60%" class="q-mx-auto q-mb-xs" />
                <q-skeleton type="text" width="80%" class="q-mx-auto q-mb-xs" />
                <q-skeleton type="text" width="40%" class="q-mx-auto" />
                <q-separator class="q-mt-md" />
              </q-card-section>
              <q-card-section class="q-gutter-sm">
                <q-skeleton type="QBtn" height="40px" class="full-width" />
                <q-skeleton type="QBtn" height="40px" class="full-width" />
              </q-card-section>
            </q-card>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="imovel-detalhes" v-else-if="imovel.title">
    <!-- Galeria de Imagens -->
    <div class="galeria-section">
      <div class="container">
        <q-btn
          flat
          round
          icon="arrow_back"
          color="primary"
          size="md"
          class="q-mb-md"
          @click="voltarLista"
        >
          <q-tooltip>Voltar</q-tooltip>
        </q-btn>

        <div class="row q-col-gutter-md">
          <!-- Imagem Principal -->
          <div class="col-12 col-md-8">
            <q-card flat class="main-image-card">
              <q-img
                :src="imagemPrincipal"
                :ratio="16 / 10"
                class="cursor-pointer"
                @click="abrirGaleria(0)"
              >
                <!-- Badge de Finalidade -->
                <div class="absolute-top-left q-ma-md">
                  <q-badge
                    :color="imovel.objective === 'Venda' ? 'green-7' : 'blue-7'"
                    class="text-bold text-uppercase q-pa-sm"
                    style="font-size: 14px"
                  >
                    {{ imovel.objective }}
                  </q-badge>
                </div>

                <!-- Badge de Estado -->
                <div class="absolute-top-right q-ma-md">
                  <q-badge
                    :color="imovel.estado === 'novo' ? 'orange-7' : 'grey-7'"
                    class="text-bold text-uppercase q-pa-sm"
                    style="font-size: 14px"
                  >
                    {{ imovel.condition }}
                  </q-badge>
                </div>

                <!-- Botões de Navegação -->
                <div
                  class="absolute-bottom row justify-between full-width q-pa-md"
                >
                  <q-btn
                    round
                    color="white"
                    text-color="grey-9"
                    icon="chevron_left"
                    @click.stop="imagemAnterior"
                  />
                  <q-btn
                    round
                    color="white"
                    text-color="grey-9"
                    icon="chevron_right"
                    @click.stop="proximaImagem"
                  />
                </div>

                <template v-slot:loading>
                  <q-spinner-gears color="primary" size="50px" />
                </template>
              </q-img>
            </q-card>
          </div>

          <!-- Miniaturas -->
          <div class="col-12 col-md-4">
            <div class="thumbnails-grid">
              <q-card
                v-for="(img, index) in imovel.imagens.slice(0, 4)"
                :key="index"
                flat
                bordered
                class="thumbnail-card cursor-pointer"
                :class="{ active: imagemAtual === index }"
                @click="selecionarImagem(index)"
              >
                <q-img :src="img" :ratio="1" />

                <!-- Overlay para "Ver mais" -->
                <div
                  v-if="index === 3 && imovel.imagens.length > 4"
                  class="thumbnail-overlay"
                >
                  <div class="text-white text-bold text-h6">
                    +{{ imovel.imagens.length - 4 }}
                  </div>
                </div>
              </q-card>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Informações Principais -->
    <div class="info-section q-py-xl">
      <div class="container">
        <div class="row q-col-gutter-xl">
          <!-- Coluna Esquerda - Detalhes -->
          <div class="col-12 col-md-8">
            <!-- Título e Preço -->
            <div class="q-mb-lg">
              <div class="row items-center justify-between q-mb-sm">
                <h1 class="text-h4 text-weight-bold text-grey-9 q-ma-none">
                  {{ imovel.title }}
                </h1>
                <!-- <q-btn
                  flat
                  round
                  icon="favorite_border"
                  color="red-6"
                  size="lg"
                  @click="toggleFavorito"
                /> -->
              </div>

              <div class="row items-center q-mb-md">
                <q-icon name="location_on" color="primary" size="20px" />
                <span class="text-body1 text-grey-7 q-ml-xs">{{
                  imovel.location
                }}</span>
              </div>

              <div class="row items-center justify-between q-mb-lg">
                <div>
                  <div class="text-caption text-grey-7">
                    {{ imovel.objective === "Venda" ? "Preço" : "Aluguel/mês" }}
                  </div>
                  <div class="text-h3 text-weight-bold text-primary">
                    {{ formatPrice(imovel.price, imovel.currency) }}
                  </div>
                </div>
                <q-chip
                  :icon="imovel.property_type === 'Casa' ? 'home' : 'apartment'"
                  color="primary"
                  text-color="white"
                  size="lg"
                  class="text-bold"
                >
                  {{ imovel.property_type }}
                </q-chip>
              </div>

              <q-separator />
            </div>

            <!-- Características Principais -->
            <div class="q-mb-xl">
              <h5 class="text-h6 text-weight-bold q-mb-md">Características</h5>
              <div class="row q-col-gutter-md">
                <div class="col-6 col-sm-4 col-md-3">
                  <q-card
                    flat
                    bordered
                    class="caracteristica-card text-center q-pa-md"
                  >
                    <q-icon name="bed" color="primary" size="32px" />
                    <div class="text-h6 text-weight-bold q-mt-sm">
                      {{ imovel.bedroom_amount }}
                    </div>
                    <div class="text-caption text-grey-7">Quartos</div>
                  </q-card>
                </div>

                <div class="col-6 col-sm-4 col-md-3">
                  <q-card
                    flat
                    bordered
                    class="caracteristica-card text-center q-pa-md"
                  >
                    <q-icon name="bathtub" color="primary" size="32px" />
                    <div class="text-h6 text-weight-bold q-mt-sm">
                      {{ imovel.bathroom_amount }}
                    </div>
                    <div class="text-caption text-grey-7">Banheiros</div>
                  </q-card>
                </div>

                <div class="col-6 col-sm-4 col-md-3">
                  <q-card
                    flat
                    bordered
                    class="caracteristica-card text-center q-pa-md"
                  >
                    <q-icon name="square_foot" color="primary" size="32px" />
                    <div class="text-h6 text-weight-bold q-mt-sm">
                      {{ imovel.area }}
                    </div>
                    <div class="text-caption text-grey-7">m²</div>
                  </q-card>
                </div>

                <div class="col-6 col-sm-4 col-md-3">
                  <q-card
                    flat
                    bordered
                    class="caracteristica-card text-center q-pa-md"
                  >
                    <q-icon name="directions_car" color="primary" size="32px" />
                    <div class="text-h6 text-weight-bold q-mt-sm">
                      {{ imovel.garage_space }}
                    </div>
                    <div class="text-caption text-grey-7">Vagas</div>
                  </q-card>
                </div>
              </div>
            </div>

            <!-- Descrição -->
            <div class="q-mb-xl">
              <h5 class="text-h6 text-weight-bold q-mb-md">Descrição</h5>
              <!-- <p class="text-body1 text-grey-8" style="line-height: 1.8">
                {{ imovel.description }}
              </p> -->
              <q-card-section class="text-body1 text-grey-8" v-html="imovel.description" style="margin-left: -16px;line-height: 1.8;"/>
            </div>

            <!-- Comodidades -->
            <!-- <div class="q-mb-xl">
              <h5 class="text-h6 text-weight-bold q-mb-md">Comodidades</h5>
              <div class="row q-col-gutter-sm">
                <div
                  v-for="comodidade in imovel.comodidades"
                  :key="comodidade"
                  class="col-6 col-sm-4"
                >
                  <q-chip
                    icon="check_circle"
                    color="green-1"
                    text-color="green-9"
                    class="full-width"
                  >
                    {{ comodidade }}
                  </q-chip>
                </div>
              </div>
            </div> -->

            <!-- Localização no Mapa -->
            <!-- <div class="q-mb-xl">
              <h5 class="text-h6 text-weight-bold q-mb-md">Localização</h5>
              <q-card flat bordered>
                <div class="map-placeholder">
                  <q-icon name="map" size="48px" color="grey-5" />
                  <div class="text-body2 text-grey-7 q-mt-sm">
                    {{ imovel.localizacao }}
                  </div>
                </div>
              </q-card>
            </div> -->
          </div>

          <!-- Coluna Direita - Contato -->
          <div class="col-12 col-md-4">
            <q-card flat bordered class="sticky-contact-card">
              <!-- Card do Agente -->
              <q-card-section class="text-center">
                <q-avatar size="80px" class="q-mb-md" v-if="imovel.user.photo">
                  <img :src="imovel.user.photo" alt="Agente" />
                </q-avatar>
                <q-avatar size="80px" class="q-mb-md" v-else>
                  <img
                    src="https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png"
                    alt="Agente"
                  />
                </q-avatar>
                <div class="text-h6 text-weight-bold text-grey-9">
                  {{ imovel.user.name }}
                </div>
                <div class="text-p text-weight-bold text-grey-9">
                  {{ imovel.user.email }}
                </div>
                <div class="text-caption text-grey-6 q-mb-sm">
                  Agente Imobiliário
                </div>
                <q-separator class="q-mt-md" />
              </q-card-section>
              <q-card-section>
                <!-- <h6 class="text-h6 text-weight-bold q-mb-md">Entre em Contato</h6> -->

                <!-- Formulário -->
                <!-- <q-form @submit="enviarMensagem" class="q-gutter-md">
                  <q-input
                    v-model="contato.nome"
                    outlined
                    dense
                    label="Nome completo"
                    :rules="[val => !!val || 'Campo obrigatório']"
                  >
                    <template v-slot:prepend>
                      <q-icon name="person" />
                    </template>
                  </q-input>

                  <q-input
                    v-model="contato.email"
                    outlined
                    dense
                    type="email"
                    label="E-mail"
                    :rules="[val => !!val || 'Campo obrigatório']"
                  >
                    <template v-slot:prepend>
                      <q-icon name="mail" />
                    </template>
                  </q-input>

                  <q-input
                    v-model="contato.telefone"
                    outlined
                    dense
                    label="Telefone"
                    :rules="[val => !!val || 'Campo obrigatório']"
                  >
                    <template v-slot:prepend>
                      <q-icon name="phone" />
                    </template>
                  </q-input>

                  <q-input
                    v-model="contato.mensagem"
                    outlined
                    type="textarea"
                    label="Mensagem"
                    rows="4"
                    :rules="[val => !!val || 'Campo obrigatório']"
                  />

                  <q-btn
                    type="submit"
                    color="primary"
                    label="Enviar Mensagem"
                    icon="send"
                    unelevated
                    class="full-width"
                  />
                </q-form> -->

                <!-- Contatos Diretos -->
                <div class="q-gutter-sm">
                  <q-btn
                    outline
                    color="green-7"
                    label="WhatsApp"
                    class="full-width"
                    v-if="imovel.user.whatsapp"
                    @click="contatoWhatsApp"
                  >
                    <template v-slot:icon>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="currentColor"
                        class="bi bi-whatsapp"
                        viewBox="0 0 16 16"
                        style="margin-right: 8px"
                      >
                        <path
                          d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"
                        />
                      </svg>
                    </template>
                  </q-btn>
                  <q-btn
                    outline
                    color="primary"
                    icon="phone"
                    label="Ligar Agora"
                    class="full-width"
                    v-if="imovel.user.phone"
                    @click="ligarAgora"
                  />
                </div>
              </q-card-section>
            </q-card>

            <!-- Card de Informações Adicionais -->
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog para Galeria -->
    <q-dialog v-model="galeriaAberta" maximized>
      <q-card class="bg-black">
        <q-bar class="bg-transparent">
          <q-space />
          <q-btn flat dense icon="close" color="white" v-close-popup />
        </q-bar>

        <q-card-section
          class="flex flex-center"
          style="height: calc(100vh - 50px)"
        >
          <q-carousel
            v-model="imagemAtual"
            animated
            navigation
            infinite
            arrows
            control-color="primary"
            class="galeria-carousel"
          >
            <q-carousel-slide
              v-for="(img, index) in imovel.imagens"
              :key="index"
              :name="index"
              :img-src="img"
            />
          </q-carousel>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import store from "@/vuex/store";

const router = useRouter();
const imagemAtual = ref(0);
const galeriaAberta = ref(false);

const imovel = ref({
  title: "",
  slug: "",
  property_type: "",
  location: "",
  price: 0,
  currency: "",
  area: 0,
  bedroom_amount: 0,
  bathroom_amount: 0,
  garage_space: 0,
  condition: "",
  objective: "",
  description: "",
  imagens: [],
  user: {
    name: "",
    phone: "",
    email: "",
    photo: "",
    whatsapp: "",
  },
});
let loader = ref(true);

const contato = ref({
  nome: "",
  email: "",
  telefone: "",
  mensagem: `Olá! Tenho interesse no imóvel - ${imovel.value.title}.`,
});

const imagemPrincipal = computed(() => imovel.value.imagens[imagemAtual.value]);

const formatPrice = (price, currency) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "MZN",
    minimumFractionDigits: 0,
  }).format(price);
};
getImovel();
function getImovel() {
  window.scrollTo(0, 0);
  store
    .dispatch(
      "properties/getPropertyBySlug",
      router.currentRoute.value.params.slug,
    )
    .then((res) => {
      imovel.value = res;
      loader.value = false;
      contato.value.mensagem = `Olá! Tenho interesse no imóvel - ${imovel.value.title}.`;
    });
}
const voltarLista = () => {
  if (window.history.state && window.history.state.back) {
    router.back();
  } else {
    router.push("/");
  }
};

const selecionarImagem = (index) => {
  imagemAtual.value = index;
};

const imagemAnterior = () => {
  if (imagemAtual.value > 0) {
    imagemAtual.value--;
  } else {
    imagemAtual.value = imovel.value.imagens.length - 1;
  }
};

const proximaImagem = () => {
  if (imagemAtual.value < imovel.value.imagens.length - 1) {
    imagemAtual.value++;
  } else {
    imagemAtual.value = 0;
  }
};

const abrirGaleria = (index) => {
  imagemAtual.value = index;
  galeriaAberta.value = true;
};

// const toggleFavorito = () => {
//   console.log('Toggle favorito');
// };

// const enviarMensagem = () => {
//   console.log('Enviar mensagem:', contato.value);
// };

const contatoWhatsApp = () => {
  const mensagem = encodeURIComponent(contato.value.mensagem);
  window.open(
    `https://wa.me/${imovel.value.user.whatsapp}?text=${mensagem}`,
    "_blank",
  );
};

const ligarAgora = () => {
  window.location.href = `tel:${imovel.value.user.phone}`;
};
</script>

<style scoped>
.imovel-detalhes {
  min-height: 100vh;
  background: #f5f5f5;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;
}

.galeria-section {
  background: white;
  padding: 24px 0;
}

.main-image-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.thumbnails-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  height: 100%;
}

.thumbnail-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
}

.thumbnail-card:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.thumbnail-card.active {
  border: 3px solid var(--q-primary);
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-section {
  background: #f5f5f5;
}

.caracteristica-card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.caracteristica-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.sticky-contact-card {
  position: sticky;
  top: 24px;
  border-radius: 16px;
}

.map-placeholder {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #e0e0e0;
}

.galeria-carousel {
  width: 90vw;
  max-width: 1200px;
  height: 80vh;
}

.galeria-carousel :deep(.q-carousel__slide) {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

@media (max-width: 1023px) {
  .sticky-contact-card {
    position: relative;
    top: 0;
  }
}
</style>
